import React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import {
  Box,
  StackLayout,
  StatusBadge,
  UtilityText,
} from '@leagueplatform/genesis-core';
import type { GDSStatus } from '@leagueplatform/genesis-core';

type Measurement = {
  value: string;
  unit?: string;
};

type RiskLabel = {
  label: string;
  style: string;
};

type MetricDetailRowProperties = {
  measurements?: Measurement[];
  riskLabel?: RiskLabel;
  referenceRange?: string;
  date: string;
  time?: string;
  source?: string;
};

export type MetricDetailRowNode = MasonryEngineNode<
  'metricDetailRow',
  MetricDetailRowProperties
>;

export type MetricDetailRowRendererProps =
  MasonryEngineNodeRendererProps<MetricDetailRowNode>;

export function MetricDetailRowRenderer({
  measurements,
  riskLabel,
  referenceRange,
  date,
  time,
  source,
  locatorId,
  rootElementRef,
}: MetricDetailRowRendererProps) {
  return (
    <Box
      ref={rootElementRef}
      data-testid={locatorId}
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottomWidth: '$thin',
        borderBottomStyle: 'solid',
        borderBottomColor: '$onSurfaceBorderSubdued',
        paddingBottom: '$one',
      }}
    >
      {/* Left side: risk label, measurements, reference range */}
      <StackLayout spacing="$quarter">
        {riskLabel && (
          <StatusBadge
            status={riskLabel.style as GDSStatus}
            label={riskLabel.label}
            showLight={false}
          />
        )}
        {measurements && measurements.length > 0 && (
          <Box
            css={{
              display: 'flex',
              gap: '$quarter',
              alignItems: 'baseline',
            }}
          >
            {measurements.map((measurement) => (
              <Box
                key={`${measurement.value}-${measurement.unit ?? 'no-unit'}`}
                css={{
                  display: 'flex',
                  gap: '$quarter',
                  alignItems: 'baseline',
                }}
              >
                <UtilityText emphasis="emphasized">
                  {measurement.value}
                </UtilityText>
                {measurement.unit && (
                  <UtilityText
                    emphasis="subtle"
                    css={{ typography: '$caption' }}
                  >
                    {measurement.unit}
                  </UtilityText>
                )}
              </Box>
            ))}
          </Box>
        )}
        {referenceRange && (
          <UtilityText emphasis="subtle" css={{ typography: '$caption' }}>
            {referenceRange}
          </UtilityText>
        )}
        {source && (
          <UtilityText emphasis="subtle" css={{ typography: '$caption' }}>
            {source}
          </UtilityText>
        )}
      </StackLayout>

      {/* Right side: date and time */}
      <StackLayout horizontalAlignment="end">
        <UtilityText>{date}</UtilityText>
        {time && (
          <UtilityText emphasis="subtle" css={{ typography: '$caption' }}>
            {time}
          </UtilityText>
        )}
      </StackLayout>
    </Box>
  );
}
